/**
 * Checks a document against the invoice requirements of § 11 UStG.
 *
 * Why it matters: an incoming invoice that is missing a mandatory detail does not entitle you to
 * the input tax deduction (Vorsteuerabzug), and the Finanzamt notices years later. Three tiers:
 *
 *  - up to 400 € gross: Kleinbetragsrechnung (§ 11 Abs 6) - a short list is enough
 *  - above that: the full list including the supplier's UID and the invoice number
 *  - above 10.000 € gross: the recipient's UID is required as well
 *
 * Quantity and description of what was supplied cannot be verified from extracted data, so it is
 * reported as a manual check rather than silently passed.
 */
import { isRateValidOn, type TaxTreatment, type VatLine } from "./vat";

export const SMALL_INVOICE_LIMIT = 400;
export const RECIPIENT_VAT_ID_LIMIT = 10_000;

export type RequirementId =
  | "supplierName"
  | "supplierAddress"
  | "supplierVatId"
  | "recipientName"
  | "recipientAddress"
  | "recipientVatId"
  | "invoiceNumber"
  | "issueDate"
  | "serviceDate"
  | "description"
  | "netAmount"
  | "vatRate"
  | "vatAmount"
  | "grossAmount";

export type Requirement = {
  id: RequirementId;
  ok: boolean;
  /** True when only a human can confirm it; the UI shows it as "please check on the document". */
  manual?: boolean;
};

export type CheckableDocument = {
  kind: "ER" | "AR" | "KA";
  invoiceNumber?: string;
  partnerName?: string;
  partnerAddress?: string;
  partnerVatId?: string;
  issueDate?: string;
  serviceDate?: string;
  netTotal?: number;
  vatTotal?: number;
  grossTotal?: number;
  vatLines?: VatLine[];
  taxTreatment?: TaxTreatment;
};

export type CheckableWorkspace = {
  legalName?: string;
  name?: string;
  street?: string;
  zip?: string;
  city?: string;
  vatId?: string;
};

export type InvoiceCheck = {
  /** Kleinbetragsrechnung: the reduced list applies. */
  simplified: boolean;
  requirements: Requirement[];
  missing: RequirementId[];
  /** Incoming document with Austrian VAT on it and something missing - the deduction is at risk. */
  inputTaxAtRisk: boolean;
};

const filled = (value: string | undefined) => Boolean(value && value.trim().length > 0);
const hasAmount = (value: number | undefined) => value !== undefined && Number.isFinite(value);

export function checkInvoice(
  document: CheckableDocument,
  workspace: CheckableWorkspace = {},
): InvoiceCheck {
  const gross = document.grossTotal ?? 0;
  const simplified = gross > 0 && gross <= SMALL_INVOICE_LIMIT;
  const outgoing = document.kind === "AR";

  // On our own invoices we are the supplier; on everything else the other party is.
  const supplier = outgoing
    ? {
        name: workspace.legalName || workspace.name,
        address: [workspace.street, [workspace.zip, workspace.city].filter(Boolean).join(" ")]
          .filter(Boolean)
          .join(", "),
        vatId: workspace.vatId,
      }
    : {
        name: document.partnerName,
        address: document.partnerAddress,
        vatId: document.partnerVatId,
      };
  const recipient = outgoing
    ? { name: document.partnerName, address: document.partnerAddress, vatId: document.partnerVatId }
    : {
        name: workspace.legalName || workspace.name,
        address: [workspace.street, [workspace.zip, workspace.city].filter(Boolean).join(" ")]
          .filter(Boolean)
          .join(", "),
        vatId: workspace.vatId,
      };

  const requirements: Requirement[] = [
    { id: "supplierName", ok: filled(supplier.name) },
    { id: "supplierAddress", ok: filled(supplier.address) },
    { id: "issueDate", ok: filled(document.issueDate) },
    { id: "description", ok: true, manual: true },
    // A rate has to exist on the day of supply: 4,9 % on a June 2026 delivery is a wrong invoice.
    {
      id: "vatRate",
      ok:
        (document.vatLines ?? []).length > 0 &&
        (document.vatLines ?? []).every((line) =>
          isRateValidOn(line.rate, document.serviceDate || document.issueDate),
        ),
    },
  ];

  if (simplified) {
    requirements.push({ id: "grossAmount", ok: hasAmount(document.grossTotal) });
  } else {
    requirements.push(
      { id: "invoiceNumber", ok: filled(document.invoiceNumber) },
      { id: "supplierVatId", ok: filled(supplier.vatId) },
      { id: "recipientName", ok: filled(recipient.name) },
      { id: "recipientAddress", ok: filled(recipient.address) },
      // The date of supply may be the invoice date; § 11 only asks that it is stated.
      { id: "serviceDate", ok: filled(document.serviceDate) || filled(document.issueDate) },
      { id: "netAmount", ok: hasAmount(document.netTotal) },
      { id: "vatAmount", ok: hasAmount(document.vatTotal) },
    );
    if (gross > RECIPIENT_VAT_ID_LIMIT) {
      requirements.push({ id: "recipientVatId", ok: filled(recipient.vatId) });
    }
  }

  const missing = requirements.filter((r) => !r.ok && !r.manual).map((r) => r.id);
  const inputTaxAtRisk =
    !outgoing &&
    (document.taxTreatment ?? "standard") === "standard" &&
    (document.vatTotal ?? 0) > 0 &&
    missing.length > 0;

  return { simplified, requirements, missing, inputTaxAtRisk };
}
